import { useRef, useState } from 'react';
import { FileImage, LayoutGrid, Loader2, Search, Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import DynamicIcon from '../../../../components/ui/DynamicIcon';
import { isImageIcon } from './IconGlyph';

const AVAILABLE_ICONS = [
  'GridIcon', 'UserCircleIcon', 'SettingsIcon', 'FileTextIcon', 'DatabaseIcon',
  'TaskIcon', 'ListIcon', 'TableIcon', 'PieChartIcon', 'BoxIcon', 'FolderIcon',
  'HomeIcon', 'UserIcon', 'LockIcon', 'EnvelopeIcon', 'CalenderIcon', 'TimeIcon',
  'CopyIcon', 'PageIcon', 'BoxCubeIcon', 'PlugInIcon', 'DocsIcon', 'MailIcon',
  'ChatIcon', 'UploadIcon', 'SearchIcon', 'FilterIcon', 'RefreshIcon', 'BoltIcon',
  'FileIcon', 'DownloadIcon', 'GroupIcon', 'ShootingStarIcon', 'DollarLineIcon',
];

interface Props {
  currentIcon?: string;
  onSelect: (icon: string) => void;
  onClose: () => void;
}

/** Sovereign Emerald icon picker — library grid + image upload. */
export default function IconPickerModal({ currentIcon, onSelect, onClose }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'icons' | 'upload'>('icons');
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredIcons = AVAILABLE_ICONS.filter((icon) =>
    icon.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const currentIsImage = isImageIcon(currentIcon);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }
    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadPreview(reader.result as string);
      setIsUploading(false);
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const tabButton = (tab: 'icons' | 'upload', icon: React.ReactNode, label: string) => {
    const active = activeTab === tab;
    return (
      <button
        type="button"
        onClick={() => setActiveTab(tab)}
        className="flex-1 px-4 py-2.5 text-[12.5px] font-semibold transition-colors"
        style={{
          color: active ? SOVEREIGN.title : SOVEREIGN.kpiCaption,
          borderBottom: active ? `2px solid ${SOVEREIGN.champagne}` : '2px solid transparent',
          background: active ? 'rgba(201,169,97,0.08)' : 'transparent',
        }}
      >
        <span className="flex items-center justify-center gap-2">
          {icon}
          {label}
        </span>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: 'rgba(42,31,23,0.45)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Select icon"
    >
      <div
        className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: SOVEREIGN.cardBg,
          border: `1px solid ${SOVEREIGN.cardBorder}`,
          boxShadow: '0 18px 44px -20px rgba(42,31,23,0.40)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
        >
          <h3 className="text-base font-extrabold" style={{ color: SOVEREIGN.title }}>
            Select icon
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: SOVEREIGN.kpiCaption }}
            aria-label="Close icon picker"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex" style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}>
          {tabButton('icons', <LayoutGrid size={15} />, 'Icon library')}
          {tabButton('upload', <Upload size={15} />, 'Upload image')}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'icons' ? (
            <>
              <div className="p-4" style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}>
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                    size={15}
                    style={{ color: SOVEREIGN.kpiCaption }}
                  />
                  <input
                    type="text"
                    placeholder="Search icons…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm rounded-lg focus:outline-none"
                    style={{
                      background: SOVEREIGN.inputBg,
                      border: `1px solid ${SOVEREIGN.inputBorder}`,
                      color: SOVEREIGN.ink,
                    }}
                  />
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2.5">
                  {filteredIcons.map((iconName) => {
                    const active = currentIcon === iconName && !currentIsImage;
                    return (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => {
                          onSelect(iconName);
                          onClose();
                        }}
                        className="p-2.5 rounded-lg transition-colors"
                        style={{
                          border: active
                            ? `2px solid ${SOVEREIGN.brand}`
                            : `1px solid ${SOVEREIGN.inputBorder}`,
                          background: active ? 'rgba(51,146,116,0.08)' : SOVEREIGN.inputBg,
                          color: active ? SOVEREIGN.bottle : SOVEREIGN.ink,
                        }}
                        title={iconName}
                      >
                        <DynamicIcon iconName={iconName} className="w-5 h-5 mx-auto" />
                      </button>
                    );
                  })}
                </div>
                {filteredIcons.length === 0 && (
                  <p className="py-8 text-center text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
                    No icons found matching "{searchQuery}"
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="p-6 space-y-4">
              {currentIsImage && (
                <div>
                  <p
                    className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em]"
                    style={{ color: SOVEREIGN.ink }}
                  >
                    Current icon
                  </p>
                  <div
                    className="flex items-center gap-3 rounded-xl p-3"
                    style={{
                      background: SOVEREIGN.inputBg,
                      border: `1px solid ${SOVEREIGN.inputBorder}`,
                    }}
                  >
                    <img src={currentIcon} alt="Current icon" className="w-12 h-12 object-contain rounded" />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: SOVEREIGN.title }}>
                        Custom uploaded icon
                      </p>
                      <p className="text-xs" style={{ color: SOVEREIGN.kpiCaption }}>
                        Upload a new image below to replace it.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <p
                  className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: SOVEREIGN.ink }}
                >
                  Upload icon
                </p>
                <div
                  className="rounded-xl p-6 text-center transition-colors"
                  style={{ border: `2px dashed ${SOVEREIGN.inputBorder}` }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="menu-config-icon-upload"
                  />
                  <label
                    htmlFor="menu-config-icon-upload"
                    className="cursor-pointer flex flex-col items-center gap-3"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-8 h-8 animate-spin" style={{ color: SOVEREIGN.brand }} />
                        <p className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
                          Processing image…
                        </p>
                      </>
                    ) : uploadPreview ? (
                      <>
                        <img
                          src={uploadPreview}
                          alt="Preview"
                          className="w-24 h-24 object-contain rounded-lg"
                          style={{ border: `1px solid ${SOVEREIGN.inputBorder}` }}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onSelect(uploadPreview);
                              onClose();
                            }}
                            className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors"
                            style={{
                              background: SOVEREIGN.btn,
                              border: `1px solid ${SOVEREIGN.btnBorder}`,
                              boxShadow: SOVEREIGN.btnShadow,
                            }}
                          >
                            Use this icon
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setUploadPreview(null);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors"
                            style={{
                              background: SOVEREIGN.inputBg,
                              border: `1px solid ${SOVEREIGN.inputBorder}`,
                              color: SOVEREIGN.title,
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center"
                          style={{ background: 'rgba(201,169,97,0.14)' }}
                        >
                          <FileImage className="w-7 h-7" style={{ color: SOVEREIGN.champagne }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: SOVEREIGN.title }}>
                            Click to upload an image
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: SOVEREIGN.kpiCaption }}>
                            PNG, JPG or GIF up to 2MB
                          </p>
                        </div>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
